/**
 * Select Control Component
 * Renders dropdown selector with static or dynamic options
 */

import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { SelectSettings } from '../../../../types/ControlTypes';
import { BaseSelect } from '../BaseSelect';
import { LabelWithInfo } from './LabelWithInfo';

interface SelectOption {
  label: string;
  value: any;
}

export const SelectControl: React.FC<ControlComponentProps> = ({
  name,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  placeholder,
  description,
  required,
  control,
}) => {
  const settings = control.settings as SelectSettings;
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(!!settings?.datasource);

  useEffect(() => {
    // If static options are provided, use them
    if (settings?.options && Array.isArray(settings.options)) {
      setOptions(settings.options);
      return;
    }

    // If datasource is provided, fetch from API
    if (settings?.datasource) {
      fetchDatasource();
    }
  }, [settings?.datasource, settings?.options]);

  const fetchDatasource = async () => {
    if (!settings?.datasource) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/globals/collection/${settings.datasource}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          limit: 100,
          searchProp: settings.renderProp || 'name'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const items = (data.data?.items || data.items || []).map((item: any) => {
          // Handle nested data structure: item.data.fieldName
          const itemData = item.data || item;
          return {
            label: itemData[settings.renderProp || 'name'] || String(itemData[settings.keyProp || 'id']),
            value: itemData[settings.keyProp || 'id'],
          };
        });
        setOptions(items);
      }
    } catch (err) {
      console.error('Failed to fetch datasource:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        <LabelWithInfo
          label={label || name}
          description={description}
          required={required}
        />
      </Box>
      <BaseSelect
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        options={options}
        placeholder={placeholder || `Select ${label || name}`}
        fullWidth
        disabled={disabled || readonly || loading}
        error={!!error}
        helperText={error}
      />
    </Box>
  );
};

export default SelectControl;
